import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import type { ColumnDef } from '@tanstack/react-table';

import { formatPrice } from 'utils/price';
import { useGetNuBasketQuery, TransactionState } from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { PAGES } from 'constants/pages';
import { Loader } from 'components/Loader';
import { SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { CopyValue } from 'components/CopyValue';
import { DetailLink, DetailValue } from 'components/DetailsView';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { TotemLabel } from 'components/TotemLabel';
import { DataTable, dateColumn, textColumn } from 'components/DataTable';
import { StatusTag } from 'pages/Transactions/components/StatusTag';

type ParamTypes = {
    nuBasketId: string;
};

type TransactionRow = {
    _id: string;
    createdAt: number;
    siteName: string;
    newState: TransactionState;
    productName: string;
    priceHT: number;
    tva: number;
};

const transactionColumns: ColumnDef<TransactionRow, unknown>[] = [
    dateColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'createdAt',
        header: 'Date',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'newState',
        header: 'Statut',
        defaultVisible: true,
        cell: ({ row }) => <StatusTag state={row.original.newState} />,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'siteName',
        header: 'Site',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        accessorKey: 'productName',
        header: 'Produit',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, Record<string, unknown>>({
        id: 'price',
        accessorFn: (row) => formatPrice(row.priceHT * (1 + row.tva)),
        header: 'Montant',
        defaultVisible: true,
    }),
];

export const NuBasket = () => {
    const { nuBasketId = '' } = useParams<ParamTypes>();

    const { data: nuBasketData, loading, error } = useGetNuBasketQuery({ variables: { nuBasketId } });

    if (loading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (error || !nuBasketData?.nuBasket) {
        throw new Error('Une erreur est survenue lors de la récupération du panier');
    }

    const { nuBasket } = nuBasketData;

    const { _id, createdAt, purchaseId, fridgeId, cardId, type, transactions, userId, siteId } = nuBasket;

    const { userFullName, siteName } = transactions?.[0] ?? { userFullName: 'inconnu', siteName: 'inconnu' };

    const sortedTransactions = [...transactions].sort(
        (transactionA, transactionB) => transactionB.createdAt - transactionA.createdAt,
    );

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.nuBasket} />
                </HeaderTitle>
            </Header>
            <Content>
                <ScrollableContent>
                    <SectionContainer title="Informations générales" keepOpen>
                        <SectionColumn>
                            <CopyValue label="Nu Basket ID" value={_id} />
                            <CopyValue label="Purchase ID" value={purchaseId} />
                            <CopyValue label="Fridge ID" value={fridgeId} />
                            <DetailValue label="Type" value={type} />
                            <DetailLink label="Utilisateur" name={userFullName} value={userId} path="user" />
                            <DetailLink label="Site" name={siteName} value={siteId} path="site" />
                            {cardId ? <DetailValue label="Card ID" value={cardId} /> : null}
                            <DetailValue label="Date de création" value={getExtendedFormattedDatetime(createdAt)} />
                            <TotemLabel>Transactions</TotemLabel>
                            <DataTable<TransactionRow>
                                columns={transactionColumns}
                                data={sortedTransactions}
                                filterMode="client"
                                getRowId={(row) => row._id}
                                getRowLink={(row) => `/transaction/${row._id}`}
                                autoFocusSearch={false}
                            />
                        </SectionColumn>
                    </SectionContainer>
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;
